/**
 * Compliance — DPDP (Digital Personal Data Protection Act 2023).
 *
 *  Tabs:
 *    1. Erasure requests — queue of resident-filed account-deletion requests.
 *       Approve / Reject / Complete actions per row. Complete is irreversible
 *       and requires typing ERASE to confirm.
 *    2. Data export log — who exported their personal data and when.
 *
 *  KPI strip across the top: pending / approved this month / completed this
 *  month / exports this month.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShieldCheck,
  Clock3,
  CheckCircle2,
  Download,
  AlertTriangle
} from 'lucide-react';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { formatDateTime } from '@/lib/utils';

type ErasureStatus = 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';

interface ErasureRequest {
  id: string;
  society_id: string;
  resident_id: string;
  reason: 'no_longer_resident' | 'privacy_concern' | 'data_inaccuracy' | 'other';
  details: string | null;
  status: ErasureStatus;
  reviewed_by_id: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  completed_at: string | null;
  data_snapshot_id: string | null;
  created_at: string;
  updated_at: string;
  resident: {
    id: string;
    full_name: string;
    phone: string;
    email: string | null;
    unit: { id: string; number: string; block: { name: string } | null } | null;
  } | null;
}

interface ErasureListResp {
  items: ErasureRequest[];
  next_cursor: string | null;
}

interface DataExportLogRow {
  id: string;
  resident_id: string;
  format: 'json' | 'csv';
  bytes: number;
  generated_at: string;
  ip: string | null;
  user_agent: string | null;
  resident: {
    id: string;
    full_name: string;
    phone: string;
    unit: { number: string; block: { name: string } | null } | null;
  } | null;
}

interface ExportLogResp {
  items: DataExportLogRow[];
}

interface KpiResp {
  pending: number;
  approved_this_month: number;
  completed_this_month: number;
  exports_this_month: number;
}

const STATUS_LABEL: Record<ErasureStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const STATUS_VARIANT: Record<ErasureStatus, 'warn' | 'ok' | 'alert' | 'muted' | 'info'> = {
  pending: 'warn',
  approved: 'info',
  rejected: 'alert',
  completed: 'ok',
  cancelled: 'muted'
};

const REASON_LABEL: Record<ErasureRequest['reason'], string> = {
  no_longer_resident: 'No longer resident',
  privacy_concern: 'Privacy concern',
  data_inaccuracy: 'Data inaccuracy',
  other: 'Other'
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function CompliancePage(): JSX.Element {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'requests' | 'exports'>('requests');
  const [statusFilter, setStatusFilter] = useState<ErasureStatus | 'all'>('all');

  // Modals
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [completeId, setCompleteId] = useState<string | null>(null);
  const [completeConfirm, setCompleteConfirm] = useState('');

  const kpis = useQuery({
    queryKey: ['dpdp', 'kpis'],
    queryFn: () => get<KpiResp>('/dpdp/kpis'),
    staleTime: 30_000
  });

  const list = useQuery({
    queryKey: ['dpdp', 'erasure-requests', statusFilter],
    queryFn: () =>
      get<ErasureListResp>('/erasure-requests', {
        params: { limit: 50, ...(statusFilter !== 'all' ? { status: statusFilter } : {}) }
      })
  });

  const exportLog = useQuery({
    queryKey: ['dpdp', 'export-log'],
    queryFn: () => get<ExportLogResp>('/dpdp/data-export-log', { params: { limit: 100 } }),
    enabled: tab === 'exports'
  });

  const approve = useMutation({
    mutationFn: (id: string) => post<ErasureRequest>(`/erasure-requests/${id}/approve`),
    onSuccess: () => {
      toast.success('Request approved');
      void qc.invalidateQueries({ queryKey: ['dpdp'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post<ErasureRequest>(`/erasure-requests/${id}/reject`, { rejection_reason: reason }),
    onSuccess: () => {
      toast.success('Request rejected');
      setRejectId(null);
      setRejectReason('');
      void qc.invalidateQueries({ queryKey: ['dpdp'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const complete = useMutation({
    mutationFn: (id: string) =>
      post<ErasureRequest>(`/erasure-requests/${id}/complete`, { confirm: 'ERASE' }),
    onSuccess: () => {
      toast.success('Erasure executed. PII redacted, financial records retained.');
      setCompleteId(null);
      setCompleteConfirm('');
      void qc.invalidateQueries({ queryKey: ['dpdp'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const items = list.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Compliance"
        description="DPDP — Right to Access (data exports) and Right to Erasure (account deletion)."
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-4">
          <KpiCard
            label="Pending requests"
            value={kpis.data?.pending}
            isLoading={kpis.isLoading}
            icon={<Clock3 className="h-5 w-5" strokeWidth={1.75} />}
            tone="warn"
          />
          <KpiCard
            label="Approved this month"
            value={kpis.data?.approved_this_month}
            isLoading={kpis.isLoading}
            icon={<CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />}
            tone="info"
          />
          <KpiCard
            label="Completed this month"
            value={kpis.data?.completed_this_month}
            isLoading={kpis.isLoading}
            icon={<ShieldCheck className="h-5 w-5" strokeWidth={1.75} />}
            tone="ok"
          />
          <KpiCard
            label="Exports this month"
            value={kpis.data?.exports_this_month}
            isLoading={kpis.isLoading}
            icon={<Download className="h-5 w-5" strokeWidth={1.75} />}
            tone="muted"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-vt-2 border-b border-vt-border">
          <TabButton active={tab === 'requests'} onClick={() => setTab('requests')}>
            Erasure requests
          </TabButton>
          <TabButton active={tab === 'exports'} onClick={() => setTab('exports')}>
            Data export log
          </TabButton>
        </div>

        {tab === 'requests' ? (
          <Card>
            <CardContent className="p-vt-4">
              {/* Status pills */}
              <div className="mb-vt-3 flex flex-wrap items-center gap-vt-2">
                <span className="text-vt-12 text-vt-text-muted">Status:</span>
                {(['all', 'pending', 'approved', 'rejected', 'completed', 'cancelled'] as const).map(
                  (s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatusFilter(s)}
                      className={
                        'rounded-vt-pill border px-3 py-1 text-vt-12 transition-colors ' +
                        (statusFilter === s
                          ? 'border-vt-primary bg-vt-primary-100 text-vt-primary font-semibold'
                          : 'border-vt-border text-vt-text-muted hover:bg-vt-surface-2')
                      }
                    >
                      {s === 'all' ? 'All' : STATUS_LABEL[s]}
                    </button>
                  )
                )}
              </div>

              {list.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : list.isError ? (
                <EmptyState
                  icon={<ShieldCheck className="h-6 w-6" strokeWidth={1.75} />}
                  title="Could not load erasure requests"
                  description={getErrorMessage(list.error)}
                />
              ) : items.length === 0 ? (
                <EmptyState
                  icon={<ShieldCheck className="h-6 w-6" strokeWidth={1.75} />}
                  title="No erasure requests"
                  description={
                    statusFilter === 'all'
                      ? 'No residents have filed DPDP erasure requests yet.'
                      : `No requests with status "${STATUS_LABEL[statusFilter as ErasureStatus]}".`
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filed</TableHead>
                      <TableHead>Resident</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-vt-text-muted">
                          {formatDateTime(r.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-vt-text">
                              {r.resident?.full_name ?? '—'}
                            </span>
                            <span className="text-vt-11 text-vt-text-muted">
                              {r.resident?.unit
                                ? `${r.resident.unit.block?.name ?? ''} ${r.resident.unit.number}`.trim()
                                : '—'}
                              {' · '}
                              {r.resident?.phone ?? '—'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-vt-13">{REASON_LABEL[r.reason]}</span>
                            {r.details ? (
                              <span className="line-clamp-2 max-w-md text-vt-11 text-vt-text-muted">
                                {r.details}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                          {r.status === 'rejected' && r.rejection_reason ? (
                            <div className="mt-1 max-w-xs text-vt-11 text-vt-text-muted">
                              {r.rejection_reason}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.status === 'pending' ? (
                            <div className="flex justify-end gap-vt-2">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => approve.mutate(r.id)}
                                disabled={approve.isPending}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setRejectId(r.id);
                                  setRejectReason('');
                                }}
                              >
                                Reject
                              </Button>
                            </div>
                          ) : r.status === 'approved' ? (
                            <Button
                              size="sm"
                              onClick={() => {
                                setCompleteId(r.id);
                                setCompleteConfirm('');
                              }}
                            >
                              Complete erasure
                            </Button>
                          ) : (
                            <span className="text-vt-11 text-vt-text-muted">No actions</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-vt-4">
              {exportLog.isLoading ? (
                <div className="flex flex-col gap-vt-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : exportLog.isError ? (
                <EmptyState
                  icon={<Download className="h-6 w-6" strokeWidth={1.75} />}
                  title="Could not load export log"
                  description={getErrorMessage(exportLog.error)}
                />
              ) : (exportLog.data?.items ?? []).length === 0 ? (
                <EmptyState
                  icon={<Download className="h-6 w-6" strokeWidth={1.75} />}
                  title="No data exports yet"
                  description="When residents download their personal data, those events appear here for auditability."
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Resident</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>IP</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(exportLog.data?.items ?? []).map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-vt-text-muted">
                          {formatDateTime(r.generated_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium text-vt-text">
                              {r.resident?.full_name ?? '—'}
                            </span>
                            <span className="text-vt-11 text-vt-text-muted">
                              {r.resident?.unit
                                ? `${r.resident.unit.block?.name ?? ''} ${r.resident.unit.number}`.trim()
                                : '—'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="muted">{r.format.toUpperCase()}</Badge>
                        </TableCell>
                        <TableCell className="text-vt-text-muted">{formatBytes(r.bytes)}</TableCell>
                        <TableCell className="font-mono text-vt-11 text-vt-text-muted">
                          {r.ip ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reject dialog */}
      <Dialog open={Boolean(rejectId)} onOpenChange={(v) => (v ? null : setRejectId(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject erasure request</DialogTitle>
            <DialogDescription>
              Give the resident a clear reason. They will see this when they re-open the screen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-vt-2 py-vt-2">
            <label className="text-vt-12 text-vt-text-muted">Rejection reason</label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Outstanding dues; please clear ₹4,800 before we can process erasure."
              maxLength={500}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectId(null)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              disabled={rejectReason.trim().length < 3 || reject.isPending}
              onClick={() => {
                if (rejectId) reject.mutate({ id: rejectId, reason: rejectReason.trim() });
              }}
            >
              {reject.isPending ? 'Rejecting…' : 'Reject request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={Boolean(completeId)} onOpenChange={(v) => (v ? null : setCompleteId(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-vt-2">
              <AlertTriangle className="h-5 w-5 text-[color:var(--vt-alert)]" strokeWidth={2} />
              Confirm erasure
            </DialogTitle>
            <DialogDescription>
              This will redact the resident&apos;s name, phone and email, soft-delete the account,
              and nullify foreign-key references to their identity. Financial records (bills +
              payments) and audit-log entries are retained as required by Indian tax + DPDP statutory
              retention. <strong>This action is irreversible.</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-vt-2 py-vt-2">
            <label className="text-vt-12 text-vt-text-muted">
              Type <code className="font-mono text-vt-text">ERASE</code> to confirm
            </label>
            <Input
              value={completeConfirm}
              onChange={(e) => setCompleteConfirm(e.target.value)}
              placeholder="ERASE"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCompleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              disabled={completeConfirm !== 'ERASE' || complete.isPending}
              onClick={() => {
                if (completeId) complete.mutate(completeId);
              }}
            >
              {complete.isPending ? 'Erasing…' : 'Erase data now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface KpiCardProps {
  label: string;
  value: number | undefined;
  isLoading: boolean;
  icon: JSX.Element;
  tone: 'warn' | 'ok' | 'info' | 'muted' | 'alert';
}

function KpiCard({ label, value, isLoading, icon, tone }: KpiCardProps): JSX.Element {
  const toneClass =
    tone === 'warn'
      ? 'bg-[color:var(--vt-warn-100)] text-vt-warn'
      : tone === 'ok'
        ? 'bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]'
        : tone === 'info'
          ? 'bg-vt-primary-100 text-vt-primary'
          : tone === 'alert'
            ? 'bg-[color:var(--vt-alert-100)] text-[color:var(--vt-alert)]'
            : 'bg-vt-surface-2 text-vt-text-muted';

  return (
    <Card>
      <CardContent className="flex items-center gap-vt-3 p-vt-4">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md ${toneClass}`}>
          {icon}
        </span>
        <div>
          <div className="text-vt-12 text-vt-text-muted">{label}</div>
          <div className="text-vt-24 font-semibold text-vt-text">
            {isLoading ? <Skeleton className="h-7 w-12" /> : value ?? 0}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'relative px-vt-4 py-vt-2 text-vt-14 transition-colors ' +
        (active
          ? 'text-vt-primary font-semibold'
          : 'text-vt-text-muted hover:text-vt-text')
      }
    >
      {children}
      {active ? (
        <span className="absolute inset-x-0 bottom-[-1px] h-0.5 rounded-full bg-vt-primary" />
      ) : null}
    </button>
  );
}

